import { makeStyles, Tooltip } from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";
import BlockIcon from "@material-ui/icons/Block";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import UnarchiveIcon from "@material-ui/icons/Unarchive";

import { useAdmin, useTenant } from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import Table, { RowActions } from "src/components/Table";
import { hasGreaterRole, hasRole, roleLabel } from "src/util/roles";

import { AdminStatus } from "./constants";
import { Admin } from "./types";

const useStyles = makeStyles(() => ({
  blockedIcon: {
    marginTop: 4
  }
}));

function InvitedCell({ admin }: { admin: Admin }) {
  return (
    <>
      {admin.invitedAtStr}
      {admin.invitedByStr ? (
        <>
          <br />
          {admin.invitedByStr}
        </>
      ) : null}
    </>
  );
}

export default function AdminTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
  handleChangeRole,
  handleDelete,
  handleUndelete,
  status
}: {
  rows: Admin[];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  handleDelete: (adminId: string) => Promise<void>;
  handleChangeRole: (admin: Admin) => void;
  handleUndelete: (admin: Admin) => void;
  status: AdminStatus;
}): JSX.Element {
  const tenant = useTenant()!;
  const loggedInAdmin = useAdmin();
  const { membership } = useSubaccount()!;
  const classes = useStyles();

  function actionsForAdmin(admin: Admin): RowActions<Admin> {
    if (admin.id === loggedInAdmin?.id) {
      // can't delete/edit self
      return [];
    }

    if (status === "active") {
      if (!hasGreaterRole(membership, admin.membership.role)) {
        return [
          {
            type: "placeholder",
            label: (
              <Tooltip title="You can’t edit users of the same or a higher role than you">
                <BlockIcon className={classes.blockedIcon} />
              </Tooltip>
            )
          }
        ];
      }

      return [
        {
          icon: <EditIcon />,
          tooltipLabel: "Change Role",
          onClick: () => handleChangeRole(admin)
        },
        {
          icon: <ArchiveIcon />,
          tooltipLabel: "Deactivate",
          onClick: () => handleDelete(admin.id),
          confirm: {
            title: "Deactivate User",
            description: `Are you sure you want to deactivate ${
              admin.fullName
            } (${admin.email})? They will immediately lose access to ${
              tenant.isUsingSubaccounts
                ? "this dialer subaccount"
                : "this dialer organization"
            }. You can reactivate them at any time.`
          }
        }
      ];
    } else if (status === "deactivated") {
      return [
        {
          icon: <UnarchiveIcon />,
          tooltipLabel: "Reactivate",
          onClick: () => handleUndelete(admin)
        }
      ];
    } else if (status === "invited") {
      if (!hasRole(membership, admin.membership.role)) {
        return [
          {
            type: "placeholder",
            label: (
              <Tooltip title="You can't rescind invites for admins who are above your role">
                <BlockIcon className={classes.blockedIcon} />
              </Tooltip>
            )
          }
        ];
      }

      return [
        {
          icon: <DeleteIcon />,
          tooltipLabel: "Rescind",
          onClick: () => handleDelete(admin.id),
          confirm: {
            title: "Rescind Invite",
            description: `Are you sure you want to rescind the invite to ${admin.fullName} (${admin.email})? They will not be able to create an account.`
          }
        }
      ];
    }

    return [];
  }

  return (
    <Table
      columns={[
        { key: "name", title: "Name", renderValue: admin => admin.fullName },
        { key: "email", title: "Email", renderValue: admin => admin.email },
        {
          key: "invited",
          title: "Invited",
          renderValue: admin => <InvitedCell admin={admin} />
        },
        {
          key: "role",
          title: "Role",
          renderValue: (admin: Admin) => roleLabel(admin.membership)
        }
      ]}
      rows={rows}
      rowActions={actionsForAdmin}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      page={page}
      onChangePage={handleChangePage}
    />
  );
}
